import type { ComponentProps } from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { classNames } from '~/utils/client/helpers'
import { useUrlQueries } from '~/utils/client/hooks/useUrlQueries'
import { TableElementsSelect } from './TableElementsSelect'

export type TablePaginationProps = {
  elementCount?: number
  currentPage?: number
  maxPerPage?: number
  setCurrentPage?: (page: number) => void
} & ComponentProps<'nav'>

type PaginationButtonProps = {
  value: 'skipBack' | 'skipNext' | 'back' | 'next' | string
  isActive: boolean
  handleClick: (value: string) => void
} & ComponentProps<'button'>

export const PaginationButton = ({
  isActive,
  value,
  children,
  className,
  handleClick,
  ...props
}: PaginationButtonProps) => {
  const activeClassName = isActive
    ? 'text-white bg-brand-hover hover:bg-border-strong hover:text-white'
    : 'text-secondary bg-white border-l border-border-default hover:bg-surface-hover hover:text-body'

  const submitValue = () => {
    handleClick(value)
  }

  return (
    <li>
      <button
        onClick={submitValue}
        className={classNames(
          'relative flex items-center justify-center text-sm py-2 px-3 leading-tight focus-ring-brand-inset-stack',
          activeClassName,
          className
        )}
        {...props}
      >
        {children}
      </button>
    </li>
  )
}

export const TablePagination = ({
  className,
  setCurrentPage,
  elementCount = 0,
  currentPage = 0,
  maxPerPage = 10,
  ...props
}: TablePaginationProps) => {
  const { setUrlQuery, getUrlQuery, submitSearchParams } = useUrlQueries()
  const totalElements = elementCount ?? 0
  const page =
    currentPage != null
      ? Number(currentPage)
      : getUrlQuery('page')
      ? Number(getUrlQuery('page'))
      : 0

  const from = page * maxPerPage + 1
  const to = Math.min((page + 1) * maxPerPage, totalElements)

  const countPages = Math.max(Math.ceil(totalElements / maxPerPage), 1)
  const isFirstPage = page <= 0
  const isLastPage = page >= countPages - 1

  // es gibt zusätzlich den Button für erste und letzte Seite
  const numButtonsToShow = 3
  const hasExtendButtonLeft = page >= 3
  const hasExtendButtonRight = page + 2 < countPages - 1 && countPages > 4

  const maximumPagesToDisplay = Math.min(countPages, numButtonsToShow)
  const pageToStartCountingFrom = page === 0 ? 0 : page + 2 > countPages ? page - 2 : page - 1

  const handlePageChange = (value: number) => {
    if (setCurrentPage) {
      return setCurrentPage(value)
    }
    setUrlQuery([{ page: value.toString() }])
    submitSearchParams()
  }

  const handleValueSubmit = (value: string) => {
    if (/^\d+$/.test(value)) {
      handlePageChange(Number(value))
    } else if (value === 'back' && !isFirstPage) {
      handlePageChange(page - 1)
    } else if (value === 'next' && !isLastPage) {
      handlePageChange(page + 1)
    } else if (value === 'skipNext' && page < countPages - 2) {
      handlePageChange(page + 2)
    } else if (value === 'skipBack' && page >= 2) {
      handlePageChange(page - 2)
    }
  }

  const renderPageButton = (pageNumber: number) => (
    <PaginationButton
      key={`table-page-${pageNumber}`}
      value={pageNumber.toString()}
      isActive={pageNumber === currentPage}
      handleClick={handleValueSubmit}
    >
      {pageNumber + 1}
    </PaginationButton>
  )

  const buttons = []

  if ((currentPage === 2 || hasExtendButtonLeft) && countPages > 3) {
    buttons.push(renderPageButton(0))
  }

  if (hasExtendButtonLeft && countPages > 4) {
    buttons.push(
      <PaginationButton
        key={'pagination-button-skip-next'}
        value={'skipBack'}
        isActive={false}
        handleClick={handleValueSubmit}
      >
        ...
      </PaginationButton>
    )
  }

  if (totalElements > 0) {
    if (countPages <= numButtonsToShow && page < 2) {
      buttons.push(
        ...Array.from({ length: maximumPagesToDisplay }, (_, index) => index).map((pageNumber) =>
          renderPageButton(pageNumber)
        )
      )
    } else {
      buttons.push(
        ...Array.from(
          { length: maximumPagesToDisplay },
          (_, index) => index + pageToStartCountingFrom
        ).map((pageNumber) => renderPageButton(pageNumber))
      )
    }
  }

  if (hasExtendButtonRight) {
    buttons.push(
      <PaginationButton
        key={'pagination-button-skip-next'}
        value={'skipNext'}
        isActive={false}
        handleClick={handleValueSubmit}
      >
        ...
      </PaginationButton>
    )
  }

  if (page < countPages - 2 && countPages > 3) {
    buttons.push(renderPageButton(countPages - 1))
  }

  return (
    <nav
      className={classNames(
        'flex flex-col md:flex-row justify-between items-start md:items-center space-y-3 md:space-y-0 p-4 gap-x-4',
        className
      )}
      aria-label="Table navigation"
      {...props}
    >
      <div className="w-full flex gap-6 items-center justify-between sm:justify-start">
        {totalElements <= 0 ? (
          <span className="text-sm font-normal text-secondary">Keine Ergebnisse</span>
        ) : (
          <span className="text-sm font-normal text-secondary">
            Ergebnisse{' '}
            <span className="font-semibold text-heading">
              {from} - {to}
            </span>{' '}
            von <span className="font-semibold text-heading">{totalElements}</span>
          </span>
        )}
        <TableElementsSelect maxElements={maxPerPage} />
      </div>

      <ul className="inline-flex items-stretch -space-x-px">
        {totalElements > 0 && (
          <PaginationButton
            value={'back'}
            isActive={false}
            disabled={isFirstPage}
            className={classNames(
              'flex items-center justify-center h-full py-1.5 px-3 ml-0 text-secondary bg-white rounded-l-lg bg-surface-hover  hover:bg-surface hover:text-body',
              isFirstPage ? 'bg-surface cursor-not-allowed' : ''
            )}
            handleClick={handleValueSubmit}
          >
            <span className="sr-only">Zurück</span>
            <ChevronLeftIcon className="w-5 h-5" />
          </PaginationButton>
        )}
        {buttons.map((button) => button)}
        {totalElements > 0 && (
          <PaginationButton
            handleClick={handleValueSubmit}
            value={'next'}
            isActive={false}
            disabled={isLastPage}
            className={classNames(
              'flex items-center justify-center h-full py-1.5 px-3 leading-tight text-secondary bg-white rounded-r-lg bg-surface-hover hover:bg-surface hover:text-body',
              isLastPage ? 'bg-surface cursor-not-allowed' : ''
            )}
          >
            <span className="sr-only">Zurück</span>
            <ChevronRightIcon className="w-5 h-5" />
          </PaginationButton>
        )}
      </ul>
    </nav>
  )
}
